import { FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { usePrevious } from '@chakra-ui/react';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useToast from 'components/ToastProvider/useToast';
import BankAccountCarousel from 'features/Bank/components/BankAccountCarousel';
import BankAccountForm from 'features/Bank/components/BankAccountForm';
import { BankAccountFormSchema } from 'features/Bank/components/BankAccountForm/bankAccountForm.schema';
import useCreateBankAccount from 'features/Bank/pages/CreateBankAccount/hooks/useCreateBankAccount';
import { getBankAccountPath } from 'features/Bank/routes';

import locales from './locales';

interface LocationState {
  returnPath?: string;
}

const CreateBankAccount: FC = () => {
  const history = useHistory();
  const { state } = useLocation<LocationState>();
  const { companyProfileId } = useCompanyContext();
  const [createBankAccount, { loading }] = useCreateBankAccount();
  const { showToast } = useToast();
  const { bankAccounts } = useCompanyBankAccounts({
    includeClosedBankAccounts: true,
  });
  const [hasSeenCarousel, setHasSeenCarousel] = useState(false);
  const prevHasSeenCarousel = usePrevious(hasSeenCarousel);

  const shouldShowCarousel = bankAccounts.length < 2 && !hasSeenCarousel;

  const handleSubmit = async (data: BankAccountFormSchema) => {
    if (!companyProfileId) {
      throw new Error('companyProfileId is missing');
    }

    try {
      const { data: createdBankAccount } = await createBankAccount({
        variables: { input: { companyProfileId, ...data } },
      });

      const createdBankAccountId =
        createdBankAccount?.createSecondaryBankAccount?.bankAccountId;

      if (createdBankAccountId) {
        history.replace(getBankAccountPath(createdBankAccountId));
      }
    } catch (error) {
      showToast({ message: locales.creationError, type: 'error' });
    }
  };

  const handleBackButtonClick = () => {
    if (hasSeenCarousel) {
      setHasSeenCarousel(false);
    } else if (state?.returnPath) {
      history.push(state.returnPath);
    } else {
      history.goBack();
    }
  };

  return (
    <>
      {shouldShowCarousel ? (
        <BankAccountCarousel
          backButtonPath={state?.returnPath}
          onCarouselEnd={() => setHasSeenCarousel(true)}
          startFromTheEnd={prevHasSeenCarousel}
        />
      ) : (
        <BankAccountForm
          loading={loading}
          onBackButtonClick={handleBackButtonClick}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default CreateBankAccount;
